// Exhaust Shrine (world.json bikerBars "exhaust"): the Kaminari-gumi garage hangout in the Hyakunin courtyard (east Okubo).
// The courtyard has no walls of its own (its north edge is open sidewalk), so the "back wall" is painted flat into the
// plaza's top rows like the other ground decals, and everything that stands (bent-pipe torii, offering shelf, parked
// bikes, drums and fire barrels) is a y-sorted prop. Presentation only: no collision, no gameplay point moves. Palette
// comes from hd-kit (theme.ts); the one custom bitmap is the 雷 sign, since the glyph table is katakana/Latin only.
// Called from hd-scene.ts (paintExhaust on the ground before the baked lights, exhaustProps with the other props).
import { WORLD, type Rect } from '../../common/src/data';
import { TILE, inTileRect } from '../../common/src/map';
import { hash2 } from '../../common/src/util';
import { canvas } from './canvas';
import { type G, K, HT, px, frame, disc, dk, lt, mix, alpha, dither, speckle, bands, shadow } from './hd-kit';
import type { LightFn } from './hd-registry';
import type { HdLight } from './hd-scene';

type Prop = (cv: HTMLCanvasElement, wx: number, wy: number, ax: number, ay: number) => void;
const bar = () => WORLD.bikerBars?.find(b => b.id === 'exhaust');

/** 雷 (thunder): rain (top bar, roof with ticks, centre stroke, four dashes) over a narrower field, 19 x 22 cells */
const RAI = [
  '.#################.',
  '.........#.........',
  '.........#.........',
  '..###############..',
  '..#......#......#..',
  '..#......#......#..',
  '....####.#.####....',
  '.........#.........',
  '....####.#.####....',
  '.........#.........',
  '.........#.........',
  '...................',
  '...................',
  '...#############...',
  '...#.....#.....#...',
  '...#.....#.....#...',
  '...#.....#.....#...',
  '...#############...',
  '...#.....#.....#...',
  '...#.....#.....#...',
  '...#.....#.....#...',
  '...#############...',
];
function bitmap(g: G, rows: string[], x: number, y: number, s: number, c: string) {
  rows.forEach((r, j) => { for (let i = 0; i < r.length; i++) if (r[i] === '#') px(g, x + i * s, y + j * s, s, s, c); });
}
/** a thick straight line of 1-px steps */
function line(g: G, x0: number, y0: number, x1: number, y1: number, t: number, c: string) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let i = 0; i <= n; i++) px(g, Math.round(x0 + (x1 - x0) * i / n), Math.round(y0 + (y1 - y0) * i / n), t, t, c);
}
function glowBlob(g: G, x: number, y: number, rx: number, ry: number, col: string, a: number) {
  g.save(); g.globalCompositeOperation = 'lighter'; g.translate(x, y); g.scale(1, ry / rx);
  const grd = g.createRadialGradient(0, 0, 0, 0, 0, rx); grd.addColorStop(0, alpha(col, a)); grd.addColorStop(1, alpha(col, 0));
  g.fillStyle = grd; g.fillRect(-rx, -rx, rx * 2, rx * 2); g.restore();
}

// ================================================================== ground (hd px, scene-relative)
export function paintExhaust(g: G, R: Rect, L: LightFn) {
  const b = bar(); if (!b || !inTileRect(b.x, b.y, R)) return;
  const ox = (b.x - R.x) * HT, oy = (b.y - R.y) * HT, W = b.w * HT, H = b.h * HT;
  g.save(); g.beginPath(); g.rect(ox, oy, W, H); g.clip(); // nothing spills onto the sidewalk or the neighbours

  // ---- oil-stained concrete: dark blotches with a thin sheen, tyre tracks sweeping in from the street
  speckle(g, ox, oy, W, H, alpha(K.night, 0.35), 0.05, 61, 2);
  for (let i = 0; i < 22; i++) {
    const cx = ox + 12 + Math.floor(hash2(i, 5) * (W - 24)), cy = oy + 92 + Math.floor(hash2(7, i) * (H - 100));
    const rx = 9 + Math.floor(hash2(i, 9) * 18), ry = 4 + Math.floor(rx * 0.45);
    disc(g, cx, cy, rx, ry, alpha(K.night, 0.4)); disc(g, cx - 2, cy, rx - 4, Math.max(2, ry - 2), alpha(K.night, 0.3));
    dither(g, cx - rx + 3, cy - ry + 1, Math.max(2, rx - 4), 2, alpha(i % 2 ? K.pink : K.cyan, 0.16));
  }
  for (const [sx, a] of [[132, 0], [140, 1], [112, 2], [330, 3], [338, 1], [400, 2]] as const) for (let t = 0; t < 150; t++) {
    const x = ox + sx + Math.round(Math.sin((t + a * 40) / 28) * 16 - t * 0.28), y = oy + H - t;
    if (t % 5 !== 4) px(g, x, y, 3, 1, alpha(K.night, 0.32));
  }
  // rubber mat under the shrine, roped off
  const mx = ox + 108, my = oy + 112, mw = 84, mh = 86;
  px(g, mx - 2, my - 2, mw + 4, mh + 4, alpha(K.ink, 0.7)); px(g, mx, my, mw, mh, K.tar); speckle(g, mx, my, mw, mh, K.tarHi, 0.06, 3);
  for (let i = 0; i < mw; i += 6) { px(g, mx + i, my, 3, 2, K.woodHi); px(g, mx + i, my + mh - 2, 3, 2, K.woodHi); }
  for (let j = 0; j < mh; j += 6) { px(g, mx, my + j, 2, 3, K.woodHi); px(g, mx + mw - 2, my + j, 2, 3, K.woodHi); }

  // ---- back wall (rows 0-2): corrugated sheet, a rolled-up garage door, trophy racks either side
  const WH = 88;
  px(g, ox, oy, W, WH, K.ink);
  const tones = [K.metalLo, K.metal, K.metalHi, K.metal];
  for (let i = 0; i < W; i += 2) px(g, ox + i, oy + 8, 2, WH - 10, tones[(i >> 1) % 4]);
  for (let i = 0; i < 18; i++) { // rust streaks
    const sx = ox + 6 + Math.floor(hash2(i, 31) * (W - 12)), sh = 10 + Math.floor(hash2(31, i) * 40);
    px(g, sx, oy + 10, 2, sh, alpha(K.woodLo, 0.55)); px(g, sx, oy + 10 + sh, 2, 3, alpha(K.woodHi, 0.5));
  }
  dither(g, ox, oy + WH - 22, W, 20, alpha(K.night, 0.35));
  px(g, ox, oy + WH - 2, W, 2, K.ink);
  px(g, ox, oy + WH, W, 3, alpha(K.night, 0.55)); px(g, ox, oy + WH + 3, W, 4, alpha(K.night, 0.25));
  // corrugated awning along the top, slats catching the light
  px(g, ox, oy, W, 9, K.ink);
  for (let i = 0; i < W; i += 6) { px(g, ox + i, oy + 1, 3, 6, K.kawaraHi); px(g, ox + i + 3, oy + 1, 3, 6, K.kawara); }
  px(g, ox, oy + 7, W, 1, K.kawaraLo);
  px(g, ox, oy + 9, W, 3, alpha(K.night, 0.4));
  for (const x of [0, W - 4]) px(g, ox + x, oy, 4, WH, K.ink);

  // garage door: slats rolled up, the shop floor glowing amber beneath
  const dx = ox + W / 2 - 56, dy = oy + 66, dw = 112, dh = WH - 66;
  px(g, dx - 3, dy - 3, dw + 6, dh + 3, K.ink);
  bands(g, dx, dy, dw, dh, dk(K.warm, 0.78), dk(K.warm, 0.3), 5);
  for (const wx of [dx + 26, dx + 76]) { // a bike wheel and a hoist silhouetted inside
    disc(g, wx, dy + dh - 8, 9, 9, K.ink); disc(g, wx, dy + dh - 8, 6, 6, dk(K.warm, 0.55));
  }
  px(g, dx + 52, dy + 6, 2, dh - 6, K.ink); px(g, dx + 46, dy + 6, 14, 2, K.ink);
  for (let j = 0; j < 14; j += 4) { px(g, dx, dy + j, dw, 3, j % 8 ? K.metal : K.metalHi); px(g, dx, dy + j + 3, dw, 1, K.ink); }
  px(g, dx + dw / 2 - 8, dy + 14, 16, 2, K.metalHi); // pull bar
  px(g, dx - 3, dy - 3, dw + 6, 2, K.metalHi);
  L(dx, dy + 14, dw, dh - 14, K.amber, 0.5, true);
  L(dx - 20, dy + dh, dw + 40, 40, K.amber, 0.3);

  // trophy rack (left): helmets on the shelf, bent plates hung below
  const lx = ox + 8;
  px(g, lx, oy + 34, 56, 3, K.ink); px(g, lx, oy + 34, 56, 1, K.woodHi);
  const hc = [K.pink, K.warm, K.white];
  for (let i = 0; i < 3; i++) {
    const cx = lx + 10 + i * 18, cy = oy + 27, c = hc[i];
    disc(g, cx, cy, 8, 7, K.ink); disc(g, cx, cy, 7, 6, c); px(g, cx - 1, cy - 5, 4, 1, lt(c, 0.5)); px(g, cx - 7, cy, 12, 4, K.ink); px(g, cx - 6, cy + 1, 10, 2, K.glassLo);
    px(g, cx + 4, cy + 2, 3, 3, K.ink);
  }
  const pc = [K.plaster, K.warmHi, K.plaster, K.white, K.plaster, K.warmHi];
  for (let i = 0; i < 6; i++) { // number plates, corners folded
    const x = lx + 2 + (i % 3) * 19, y = oy + 42 + Math.floor(i / 3) * 14;
    px(g, x, y, 16, 9, K.ink); px(g, x + 1, y + 1, 14, 7, pc[i]); speckle(g, x + 3, y + 3, 10, 3, K.ink, 0.45, i + 2);
    px(g, x + 12, y + 1, 4, 3, K.ink); px(g, x + 12, y + 1, 3, 2, dk(pc[i], 0.4)); // bent corner
    px(g, x + 7, y - 1, 2, 2, K.metalHi);
  }
  // second trophy rack right of the door: an amber helmet row over a bent-plate grid
  const tx0 = ox + W / 2 + 76;
  px(g, tx0, oy + 34, 80, 3, K.ink); px(g, tx0, oy + 34, 80, 1, K.woodHi);
  for (let i = 0; i < 4; i++) {
    const cx = tx0 + 10 + i * 20, cy = oy + 27, c = [K.warm, K.cyan, K.pink, K.white][i];
    disc(g, cx, cy, 8, 7, K.ink); disc(g, cx, cy, 7, 6, c); px(g, cx - 1, cy - 5, 4, 1, lt(c, 0.5)); px(g, cx - 7, cy, 12, 4, K.ink); px(g, cx - 6, cy + 1, 10, 2, K.glassLo);
  }
  for (let i = 0; i < 8; i++) {
    const x = tx0 + 2 + (i % 4) * 19, y = oy + 42 + Math.floor(i / 4) * 14;
    px(g, x, y, 16, 9, K.ink); px(g, x + 1, y + 1, 14, 7, pc[(i + 1) % 6]); speckle(g, x + 3, y + 3, 10, 3, K.ink, 0.45, i + 20); px(g, x + 12, y + 1, 4, 3, K.ink);
  }
  // tool wall left of the door: pegboard with hung spanners, a hacksaw and an oil can
  const pg = ox + 84, pw = W / 2 - 56 - 84 - 14;
  px(g, pg, oy + 24, pw, 52, K.ink); px(g, pg + 1, oy + 25, pw - 2, 50, K.metalLo);
  for (let j = 28; j < 72; j += 6) for (let i = 4; i < pw - 4; i += 6) px(g, pg + i, oy + j, 1, 1, K.ink);
  for (let i = 0; i < 7 && 8 + i * 11 < pw - 12; i++) { const x = pg + 8 + i * 11, h = 14 + (i * 5) % 12; px(g, x, oy + 30, 3, h, K.metalHi); disc(g, x + 1, oy + 30 + h, 3, 3, K.metalHi); px(g, x + 1, oy + 30 + h - 1, 1, 2, K.metalLo); px(g, x, oy + 29, 3, 2, K.ink); }
  px(g, pg + 6, oy + 60, 26, 2, K.ink); px(g, pg + 6, oy + 61, 26, 1, K.metalHi); px(g, pg + 36, oy + 56, 12, 14, K.ink); px(g, pg + 37, oy + 57, 10, 12, K.warm); px(g, pg + 48, oy + 58, 6, 2, K.ink);
  // bike front end bolted up at the east end
  const rx0 = ox + W - 64;
  // the front half of a motorbike bolted to the wall: headlight, forks, fat wheel, tank and engine block
  g.save(); g.beginPath(); g.rect(rx0, oy + 24, 64, 56); g.clip();
  g.translate(rx0 + 2 - 32, oy + 26 - 4); drawBike(g, K.pink); g.restore();
  px(g, rx0 - 2, oy + 24, 2, 54, K.ink);
  for (let i = 0; i < 3; i++) { // plates along the top
    const x = rx0 + 2 + i * 21, y = oy + 14;
    px(g, x, y, 18, 9, K.ink); px(g, x + 1, y + 1, 16, 7, pc[(i + 3) % 6]); speckle(g, x + 3, y + 3, 12, 3, K.ink, 0.45, i + 9); px(g, x + 14, y + 1, 4, 3, K.ink);
  }

  // string lights sagging along the wall, bulbs alternating amber and pink
  for (let s = 0; s < W / 64; s++) {
    const x0 = ox + s * 64;
    for (let t = 0; t <= 64; t += 2) px(g, x0 + t, oy + 11 + Math.round(5 * Math.sin(Math.PI * t / 64)), 2, 1, K.ink);
    for (let t = 8; t < 64; t += 12) {
      const y = oy + 12 + Math.round(5 * Math.sin(Math.PI * t / 64)), c = (t / 12 + s) % 2 < 1 ? K.warmHi : K.pink;
      px(g, x0 + t - 1, y, 3, 4, K.ink); px(g, x0 + t, y + 1, 1, 2, c);
      glowBlob(g, x0 + t, y + 2, 7, 6, c, 0.5);
    }
    L(x0 + 8, oy + 12, 48, 14, s % 2 ? K.pink : K.amber, 0.22);
  }

  // the 雷 neon: a plate hung from the awning, bright pink core
  const sw = 60, sx = ox + W / 2 - sw / 2, sy = oy + 9, sh = 56;
  px(g, sx, sy, sw, sh, K.ink); px(g, sx + 1, sy + 1, sw - 2, sh - 2, dk(K.pink, 0.88));
  frame(g, sx + 2, sy + 2, sw - 4, sh - 4, dk(K.pink, 0.2)); frame(g, sx + 3, sy + 3, sw - 6, sh - 6, dk(K.pink, 0.6));
  const gx = sx + 11, gy = sy + 6; // 19 x 22 cells of 2 px: strokes survive the bloom
  for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, 1], [-1, 1], [1, -1]]) bitmap(g, RAI, gx + dx, gy + dy, 2, K.ink); // dark halo
  bitmap(g, RAI, gx, gy, 2, mix(K.pink, K.white, 0.3));
  px(g, sx + 8, oy + 7, 2, 3, K.ink); px(g, sx + sw - 10, oy + 7, 2, 3, K.ink);
  L(sx, sy, sw, sh, K.pink, 0.45, true, true);
  L(sx - 30, sy + sh, 120, 60, K.pink, 0.2);
  glowBlob(g, sx + sw / 2, sy + sh / 2, 40, 28, K.pink, 0.12);

  // curb strip along the open south edge, with a faded hazard stripe
  for (let i = 0; i < W; i += 12) px(g, ox + i, oy + H - 3, 6, 2, alpha(K.tactile, 0.55));
  g.restore();
}

// ================================================================== props (y-sorted, no collision)
export interface Sprite { cv: HTMLCanvasElement; ax: number; ay: number }

/** an oil drum; lit = a fire in it, otherwise a pink-banded plain drum */
export function barrel(lit: boolean): Sprite {
  const [cv, g] = canvas(36, 62), ax = 18, ay = 58;
  shadow(g, 6, 54, 28, 6, 0.4);
  px(g, 5, 28, 26, 28, K.ink); px(g, 6, 29, 24, 26, K.metal);
  px(g, 6, 29, 4, 26, K.metalHi); px(g, 26, 29, 4, 26, K.metalLo);
  for (const y of [34, 46]) { px(g, 5, y, 26, 3, K.ink); px(g, 6, y + 1, 24, 1, K.metalLo); }
  if (!lit) px(g, 6, 38, 24, 7, dk(K.pink, 0.2));
  speckle(g, 6, 29, 24, 26, K.woodLo, 0.07, 4);
  disc(g, 18, 28, 13, 5, K.ink); disc(g, 18, 28, 11, 4, lit ? K.ink : K.tarHi);
  if (lit) {
    glowBlob(g, 18, 20, 20, 20, K.warm, 0.5);
    // smoke first, then ragged tongues: red edge, orange body, yellow and white-hot at the middle
    for (const [x, y, w] of [[10, 1, 6], [20, 0, 7], [15, 3, 5]] as const) dither(g, x, y, w, 3, alpha(K.night, 0.4), x);
    const hs = [3, 6, 9, 13, 8, 15, 21, 16, 11, 19, 24, 20, 13, 17, 12, 8, 12, 7, 5, 3, 2];
    hs.forEach((h, i) => {
      const x = 8 + i, f = 1 - Math.abs(i - 10) / 11, base = 28;
      px(g, x, base - h, 1, h, K.red);
      px(g, x, base - Math.round(h * 0.82), 1, Math.round(h * 0.82), K.warm);
      if (f > 0.15) px(g, x, base - Math.round(h * 0.6 * f + 2), 1, Math.round(h * 0.6 * f + 2), K.warmHi);
      if (f > 0.45) px(g, x, base - Math.round(h * 0.3 * f + 1), 1, Math.round(h * 0.3 * f + 1), K.white);
    });
    for (const [x, y] of [[6, 12], [30, 9], [12, 4], [25, 3], [4, 20], [32, 17]] as const) px(g, x, y, 1, 1, hash2(x, y) > 0.5 ? K.warmHi : K.warm); // sparks
  }
  return { cv, ax, ay };
}

/** a parked bike seen from the side (96 x 52), facing right unless flipped */
function bike(body: string, flip: boolean): Sprite {
  const W = 96, [cv, g] = canvas(W, 52), ax = 48, ay = 48;
  if (flip) { g.translate(W, 0); g.scale(-1, 1); }
  shadow(g, 8, 46, 82, 5, 0.4);
  drawBike(g, body);
  return { cv, ax, ay };
}
/** the bike body, drawn with its top-left at the context origin (the wall rack reuses it) */
function drawBike(g: G, body: string) {
  for (const cx of [16, 80]) { // wheels
    disc(g, cx, 36, 12, 12, K.ink); disc(g, cx, 36, 10, 10, K.tarHi); disc(g, cx, 36, 7, 7, K.metalHi); disc(g, cx, 36, 5, 5, K.tar);
    for (let a = 0; a < 4; a++) line(g, cx + Math.round(Math.cos(a * Math.PI / 4) * 6), 36 + Math.round(Math.sin(a * Math.PI / 4) * 6), cx - Math.round(Math.cos(a * Math.PI / 4) * 6), 36 - Math.round(Math.sin(a * Math.PI / 4) * 6), 1, K.metalLo);
    disc(g, cx, 36, 2, 2, K.ink);
  }
  line(g, 80, 36, 68, 14, 3, K.ink); line(g, 80, 36, 68, 14, 1, K.metalHi); // fork
  line(g, 16, 36, 40, 30, 3, K.ink); line(g, 16, 36, 40, 30, 1, K.metal); // swingarm
  px(g, 36, 28, 24, 12, K.ink); px(g, 37, 29, 22, 10, K.metal); px(g, 37, 29, 22, 2, K.metalHi); px(g, 42, 34, 12, 2, K.metalLo); // engine
  px(g, 34, 14, 28, 15, K.ink); px(g, 35, 15, 26, 13, body); px(g, 35, 15, 26, 3, lt(body, 0.35)); px(g, 35, 24, 26, 3, dk(body, 0.3)); // tank
  px(g, 62, 10, 6, 6, K.ink); px(g, 63, 11, 4, 4, dk(body, 0.2)); // upper fairing
  px(g, 14, 18, 24, 8, K.ink); px(g, 15, 19, 22, 6, K.tar); px(g, 15, 19, 22, 1, K.tarHi); // seat
  px(g, 8, 20, 10, 4, K.ink); px(g, 9, 21, 8, 2, body); // tail
  px(g, 64, 6, 14, 3, K.ink); px(g, 66, 7, 10, 1, K.metalHi); px(g, 76, 5, 3, 5, K.ink); // bars
  disc(g, 74, 18, 4, 4, K.ink); disc(g, 74, 18, 3, 3, K.warmHi); px(g, 73, 17, 2, 2, K.white); // headlight
  line(g, 44, 40, 14, 42, 4, K.ink); line(g, 44, 40, 14, 42, 2, K.metalHi); px(g, 10, 40, 5, 5, K.ink); px(g, 11, 41, 3, 3, K.metalLo); // chrome pipe
}

/** the shrine's gate: a torii of bent exhaust pipes, chrome with heat-blued rings and a paper-streamer rope */
function torii(): Sprite {
  const W = 100, [cv, g] = canvas(W, 92), ax = 50, ay = 88;
  shadow(g, 10, 84, 82, 5, 0.4);
  const pipe = (x: number, y: number, w: number, h: number) => {
    px(g, x - 1, y - 1, w + 2, h + 2, K.ink); px(g, x, y, w, h, K.metal);
    px(g, x, y, 2, h, K.metalHi); px(g, x + w - 2, y, 2, h, K.metalLo);
  };
  for (const x of [14, 78]) { // pillars: chrome tube, blued rings, flange foot
    pipe(x, 20, 8, 64);
    for (const [y, c] of [[40, K.cyan], [58, K.woodHi], [72, K.pink]] as const) { px(g, x - 1, y, 10, 3, K.ink); px(g, x, y + 1, 8, 1, dk(c, 0.4)); }
    px(g, x - 3, 82, 14, 4, K.ink); px(g, x - 2, 83, 12, 2, K.metalLo);
  }
  pipe(10, 28, 80, 5); // nuki
  pipe(4, 12, 92, 6); // kasagi
  px(g, 1, 8, 8, 5, K.ink); px(g, 2, 9, 6, 3, K.metal); px(g, 91, 8, 8, 5, K.ink); px(g, 92, 9, 6, 3, K.metal); // upturned ends
  px(g, 0, 6, 4, 4, K.ink); px(g, 96, 6, 4, 4, K.ink); // dark soot-black pipe mouths
  for (const x of [22, 46, 70]) { px(g, x, 11, 3, 8, K.ink); px(g, x, 12, 3, 1, K.metalHi); } // clamps
  px(g, 38, 20, 24, 6, K.ink); px(g, 39, 21, 22, 4, dk(K.pink, 0.5)); px(g, 41, 22, 18, 1, K.pink); // plaque, a bare pink tube
  for (let x = 24; x < 76; x += 3) px(g, x, 34 + (x % 6 ? 1 : 0), 3, 2, K.woodHi); // shimenawa rope
  for (const x of [32, 44, 56, 68]) { px(g, x, 36, 4, 8, K.ink); px(g, x + 1, 37, 2, 6, K.white); px(g, x + 1, 41, 3, 2, K.plaster); }
  return { cv, ax, ay };
}

/** a low offering shelf: sake cups, a bottle and candles */
function shelf(): Sprite {
  const W = 64, [cv, g] = canvas(W, 40), ax = 32, ay = 36;
  shadow(g, 4, 32, 58, 5, 0.4);
  for (const x of [6, 52]) px(g, x, 24, 5, 10, K.ink);
  px(g, 2, 20, 60, 6, K.ink); px(g, 3, 21, 58, 4, K.woodHi); px(g, 3, 21, 58, 1, lt(K.woodHi, 0.3)); px(g, 3, 24, 58, 1, K.woodLo);
  const cup = (x: number) => { px(g, x, 14, 7, 7, K.ink); px(g, x + 1, 15, 5, 5, K.white); px(g, x + 1, 15, 5, 2, K.glassHi); px(g, x + 2, 20, 3, 1, K.ink); };
  cup(10); cup(20); cup(42);
  px(g, 31, 6, 6, 15, K.ink); px(g, 32, 7, 4, 13, K.glassLo); px(g, 32, 12, 4, 6, K.leaf); px(g, 33, 3, 2, 4, K.ink); // bottle
  for (const x of [52, 4]) { // candles
    px(g, x, 12, 4, 9, K.ink); px(g, x + 1, 13, 2, 8, K.white);
    px(g, x + 1, 6, 2, 5, K.warm); px(g, x + 1, 5, 2, 2, K.warmHi); glowBlob(g, x + 2, 8, 10, 10, K.warm, 0.45);
  }
  return { cv, ax, ay };
}

/** a workbench with a vice, a spanner and a clamp lamp */
function bench(): Sprite {
  const [cv, g] = canvas(76, 48), ax = 38, ay = 44;
  shadow(g, 4, 40, 70, 5, 0.4);
  for (const x of [6, 64]) px(g, x, 24, 5, 18, K.ink);
  px(g, 2, 20, 72, 7, K.ink); px(g, 3, 21, 70, 5, K.woodHi); px(g, 3, 21, 70, 1, lt(K.woodHi, 0.3)); px(g, 3, 25, 70, 1, K.woodLo);
  px(g, 8, 28, 60, 3, K.metalLo); // shelf rail
  px(g, 10, 12, 10, 9, K.ink); px(g, 11, 13, 8, 7, K.metalHi); px(g, 14, 8, 3, 5, K.ink); px(g, 12, 9, 7, 2, K.metalLo); // vice
  line(g, 30, 19, 48, 19, 2, K.metalHi); disc(g, 50, 19, 3, 3, K.metalHi); disc(g, 50, 19, 1, 1, K.ink); // spanner
  px(g, 56, 4, 2, 17, K.ink); px(g, 58, 4, 10, 2, K.ink); disc(g, 66, 8, 3, 3, K.warmHi); glowBlob(g, 66, 10, 12, 10, K.warm, 0.5); // clamp lamp
  return { cv, ax, ay };
}
/** a sofa made of two bucket seats from wrecked cars on cinder blocks */
function sofa(): Sprite {
  const [cv, g] = canvas(64, 40), ax = 32, ay = 36;
  shadow(g, 4, 31, 58, 5, 0.4);
  for (const x of [4, 44]) { px(g, x, 30, 14, 5, K.ink); px(g, x + 1, 31, 12, 3, K.concrete); }
  for (const x of [6, 34]) {
    px(g, x - 1, 4, 24, 28, K.ink); px(g, x, 5, 22, 26, K.red); px(g, x, 5, 22, 3, lt(K.red, 0.3)); // backrest
    px(g, x + 3, 9, 16, 1, K.redLo); px(g, x + 3, 15, 16, 1, K.redLo); px(g, x + 10, 6, 1, 20, K.redLo);
    px(g, x - 1, 22, 24, 10, K.ink); px(g, x, 23, 22, 8, dk(K.red, 0.2)); px(g, x, 23, 22, 2, K.red); // cushion
    px(g, x + 2, 28, 18, 1, K.redLo);
  }
  px(g, 28, 18, 8, 5, K.ink); px(g, 29, 19, 6, 3, K.metal); // shared armrest
  return { cv, ax, ay };
}
/** a drinks vending machine lit cyan, cans in pink and amber */
function vendor(): Sprite {
  const [cv, g] = canvas(30, 56), ax = 15, ay = 52;
  shadow(g, 3, 48, 26, 5, 0.4);
  px(g, 2, 2, 26, 48, K.ink); px(g, 3, 3, 24, 46, dk(K.cyan, 0.5));
  px(g, 5, 5, 20, 26, K.ink); px(g, 6, 6, 18, 24, K.glass);
  for (let j = 0; j < 3; j++) for (let i = 0; i < 4; i++) { px(g, 7 + i * 4, 8 + j * 8, 3, 6, [K.pink, K.warm, K.cyan, K.white][(i + j) % 4]); px(g, 7 + i * 4, 8 + j * 8, 3, 1, lt(K.white, 0)); }
  px(g, 6, 6, 18, 2, K.glassHi);
  px(g, 6, 34, 12, 4, K.ink); px(g, 7, 35, 10, 2, K.pink); px(g, 20, 34, 5, 8, K.ink); px(g, 21, 35, 3, 6, K.metalHi);
  px(g, 6, 42, 18, 5, K.ink); px(g, 7, 43, 16, 3, K.glassLo);
  px(g, 3, 3, 2, 46, K.cyan);
  return { cv, ax, ay };
}

export function exhaustProps(R: Rect, prop: Prop, lights: HdLight[]) {
  const b = bar(); if (!b || !inTileRect(b.x, b.y, R)) return;
  const bx = b.x * TILE, by = b.y * TILE;
  const put = (s: Sprite, u: number, v: number) => prop(s.cv, bx + u / 2, by + v / 2, s.ax, s.ay);
  const pool = (u: number, v: number, w: number, h: number, color: string, a: number) =>
    lights.push({ x: bx + u / 2 - w / 2, y: by + v / 2 - h / 2, w, h, color, alpha: a, pool: true });
  const fire = barrel(true), drum = barrel(false), mk = (body: string, flip: boolean) => bike(body, flip);
  put(torii(), 150, 160); put(shelf(), 150, 184);
  put(fire, 22, 224); put(fire, 300, 204); put(fire, 490, 228);
  put(drum, 420, 112); put(drum, 438, 118);
  // front row of bikes nosed in to the wall side, a second row behind
  put(mk(K.pink, false), 260, 146); put(mk(K.warm, true), 330, 150); put(mk(K.pink, true), 396, 154);
  put(mk(K.warm, false), 240, 218); put(mk(K.pink, false), 340, 232); put(mk(K.warm, true), 424, 208);
  put(bench(), 470, 124); put(sofa(), 52, 172); put(vendor(), 498, 176);
  pool(22, 214, 60, 28, K.amber, 0.55); pool(300, 194, 60, 28, K.amber, 0.55); pool(490, 218, 60, 28, K.amber, 0.55);
  pool(150, 176, 26, 12, K.amber, 0.4); pool(150, 150, 70, 40, K.pink, 0.16);
  pool(260, 146, 60, 24, K.pink, 0.18); pool(396, 154, 60, 24, K.pink, 0.14);
  pool(470, 112, 40, 20, K.amber, 0.4); pool(498, 170, 34, 40, K.cyan, 0.3);
}
